import { IMemberDepricated, IMinUserDepricated, IPNSProps, IUserDepricated } from "@mctypes/global.types";
import { ColDef } from "ag-grid-community";
import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, CardTitle, UncontrolledTooltip } from "reactstrap";
import {
  Checkbox,
  HSpace,
  McDropdown,
  McDropdownSingleUser,
  McInput,
  McTypeahead,
  SimpleTagsDeprecated,
  VSpace,
} from "../../../components/";
import { UserCellRenderer } from "../../../components/ConnectedIssues/ConnectedIssues";
import ViewEditTable from "../../../components/ViewEditTable/ViewEditTable";
import { setShowSuccessMessageThunk } from "../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { ChecklistDisciplineRoles } from "../../../mc-shared/enums";
import { ToastMessagesEnums } from "../../../mc-shared/sharedConstants";
import ChecklistRemove from "./admin/ChecklistRemove";
import ChecklistTypes from "./admin/ChecklistTypes";
import StampConfiguration from "./admin/StampConfiguration";
import { useGetUniqueChecklistTagsQuery } from "./checklistRTK";
import { updateChecklistThunk } from "./checklistSlice";
import { ChecklistStatusEnum, CHECKLISTSTATUSES, competenceNetworkList, TEMPLATESTATUSES } from "./constants";

const ChecklistAdmin: React.FC = () => {
  const dispatch = useAppDispatch();
  const membersInRedux: IMemberDepricated[] = useAppSelector((state) => state.adminReducer.project.members);
  const project = useAppSelector((state) => state.adminReducer.project);
  const checklist = useAppSelector((state) => state.checklistReducer.checklist);
  const pnsItemsInRedux = useAppSelector((state) => state.adminReducer.project.pns);
  const [pnsItems, setPnsItems] =
    useState<{ comboLabel: string; _id: string; parentId: string; props: IPNSProps }[]>(null);

  const [removed, setRemoved] = useState<boolean>(false);

  const updateChecklist = async (prop: string, oldVal, newVal) => {
    dispatch(updateChecklistThunk(prop, newVal, oldVal));
    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.SETTINGS_UPDATED));
  };

  useEffect(() => {
    setRemoved(checklist.status === ChecklistStatusEnum.REMOVED);
  }, [checklist.status]);

  useEffect(() => {
    setPnsItems(
      pnsItemsInRedux?.map((pns) => ({
        ...pns,
        comboLabel: `${pns.props.pnsId} - ${pns.props.title}`,
      })),
    );
  }, []);

  const { data: tags = [] } = useGetUniqueChecklistTagsQuery({ projectId: project._id });

  // Name must always be a string or crash in ag-grid cell editor when autocomplete is selecting empty/null user
  const emptyDocManager = { name: "", _id: "" } as IUserDepricated;
  const isNullOrEmpty = (value) => value == null || value === "";

  return (
    <div id="a" className="h-100 w-100 p-2 overflow-auto">
      {checklist.isTemplate === true && (
        <Card>
          <CardBody style={{ maxWidth: "35rem" }}>
            <CardTitle tag="h3">Malinstillinger</CardTitle>
            <DocumentManagers
              onSave={(roles) =>
                updateChecklist(
                  "documentManagers",
                  {
                    preparedBy: checklist.documentManagers?.preparedBy?.name ?? "",
                    checkedBy: checklist.documentManagers?.checkedBy?.name ?? "",
                    approvedBy: checklist.documentManagers?.approvedBy?.name ?? "",
                  },
                  {
                    preparedBy: isNullOrEmpty(roles.preparedBy?._id) === false ? roles.preparedBy : null,
                    checkedBy: isNullOrEmpty(roles.checkedBy?._id) === false ? roles.checkedBy : null,
                    approvedBy: isNullOrEmpty(roles.approvedBy?._id) === false ? roles.approvedBy : null,
                  },
                )
              }
              documentManagers={{
                preparedBy: checklist.documentManagers?.preparedBy ?? emptyDocManager,
                checkedBy: checklist.documentManagers?.checkedBy ?? emptyDocManager,
                approvedBy: checklist.documentManagers?.approvedBy ?? emptyDocManager,
              }}
              projectMembers={[
                {
                  user: emptyDocManager,
                  isPassive: false,
                  disciplin: "",
                  group: null,
                },
                ...membersInRedux,
              ]}
            />
            <div>
              <h5 className="mb-1 mt-2">Admin</h5>
              <McDropdownSingleUser
                color="secondary"
                onClear={
                  checklist.admin?.name != null ? () => updateChecklist("admin", checklist.admin.name, null) : null
                }
                filterActive={true}
                options={membersInRedux.map((member) => member.user as IUserDepricated)}
                selected={checklist.admin}
                onChange={(user) => updateChecklist("admin", checklist.admin, user._id)}
              />
            </div>
            <VSpace />
            <div>
              <h5 className="mb-1">FLF</h5>
              <McDropdownSingleUser
                color="secondary"
                onClear={
                  checklist.disciplineLeader?.name != null
                    ? () => updateChecklist("disciplineLeader", checklist.disciplineLeader.name, null)
                    : null
                }
                filterActive={true}
                options={membersInRedux.map((member) => member.user as IUserDepricated)}
                selected={checklist.disciplineLeader}
                onChange={(user) => updateChecklist("disciplineLeader", checklist.disciplineLeader, user._id)}
              />
            </div>

            <VSpace />
            <div>
              <h5 className="mb-1">Fagdisiplin</h5>
              <McDropdown
                onClear={
                  checklist.disciplineCode != null
                    ? () => updateChecklist("disciplineCode", checklist.disciplineCode, null)
                    : null
                }
                filterActive={true}
                color="secondary"
                displayKey="title"
                currentValue={ChecklistDisciplineRoles.find((disc) => disc.code === checklist.disciplineCode)}
                onChange={(_, code) => updateChecklist("disciplineCode", checklist.disciplineCode, code.code)}
                options={ChecklistDisciplineRoles}
              />
            </div>
            <VSpace />
            <div>
              <h5 className="mb-1">Delfagområde</h5>
              <McInput
                type="text"
                onBlur={(e) => updateChecklist("subDiscipline", checklist.subDiscipline, e.target.value)}
                value={checklist.subDiscipline}
                disabled={removed === true}
              />
            </div>
            <VSpace />
            <div>
              <h5 className="mb-1">Dokumenttype</h5>
              <McInput
                type="text"
                onBlur={(e) => updateChecklist("documentCategoryCode", checklist.documentCategoryCode, e.target.value)}
                value={checklist.documentCategoryCode ?? "SKL"}
                disabled={true}
              />
            </div>
            <VSpace />
            <div>
              <h5 className="mb-1">Dokumenttype 2</h5>
              <McInput
                type="text"
                onBlur={(e) =>
                  updateChecklist("documentCategoryCode2", checklist.documentCategoryCode2, e.target.value)
                }
                value={checklist.documentCategoryCode2}
                disabled={removed === true}
              />
            </div>
            <VSpace />
            <div>
              <h5 className="mb-1">Kompetansenettverk</h5>
              <McDropdown
                currentValue={competenceNetworkList.find((s) => s === checklist.competenceNetwork)}
                options={competenceNetworkList}
                displayKey="label"
                onChange={(oldValue, newValue) => updateChecklist("competenceNetwork", oldValue, newValue)}
                disabled={removed === true}
              />
            </div>
            <VSpace />
            <div>
              <h5 className="mb-1">Revisionsnr</h5>
              <McInput
                type="text"
                onBlur={(e) => updateChecklist("revisionNr", checklist.revisionNr, e.target.value)}
                value={checklist.revisionNr}
                disabled={removed === true}
              />
            </div>
            <VSpace />
            <div>
              <h5 className="mb-1">Revisionsdato</h5>
              <McInput
                type="date"
                onBlur={(e) => updateChecklist("revisionDate", checklist.revisionDate, e.target.value)}
                value={checklist.revisionDate}
                disabled={removed === true}
              />
            </div>
            <VSpace />
            <div>
              <h5 className="mb-1">Malbeskrivelse</h5>
              <McInput
                type="textarea"
                value={checklist.templateDescription ?? ""}
                onBlur={(e) => updateChecklist("templateDescription", checklist.templateDescription, e.target.value)}
                disabled={removed === true}
                min={3}
                dynamicHeight={true}
              />
            </div>
          </CardBody>
        </Card>
      )}

      <VSpace />

      <Card>
        <CardBody style={{ maxWidth: "35rem" }}>
          <CardTitle tag="h3">Innstillinger</CardTitle>
          <div>
            <h5 className="mb-1">Navn</h5>
            <McInput
              required={true}
              onBlur={(e) => updateChecklist("name", checklist.name, e.target.value)}
              value={checklist.name}
              disabled={removed === true}
            />
          </div>
          <VSpace />
          <div>
            <h5 className="mb-1">Status</h5>
            <McDropdown
              currentValue={(checklist.isTemplate === true ? TEMPLATESTATUSES : CHECKLISTSTATUSES).find(
                (s) => s.value === checklist.status,
              )}
              options={
                checklist.isTemplate === true
                  ? TEMPLATESTATUSES.filter((x) => x.value !== "REMOVED")
                  : CHECKLISTSTATUSES.filter((x) => x.value !== "REMOVED")
              }
              displayKey="label"
              onChange={(oldValue, newValue) => updateChecklist("status", oldValue?.value, newValue.value)}
              disabled={removed === true}
            />
          </div>
          <VSpace />
          <div>
            <h5 className="mb-1">Fra mal</h5>
            <McInput disabled={true} value={checklist.generatedFromTemplate?.name ?? ""} />
          </div>
          <VSpace />
          <div>
            <h5 className="mb-1">Etiketter på sjekkliste</h5>
            <SimpleTagsDeprecated
              editable={removed === false}
              activeTags={checklist.tags || []}
              tagOptions={tags.map((t) => t._id)}
              onChange={(tags) => updateChecklist("tags", checklist.tags, tags)}
            />
          </div>
          <VSpace />
          <div>
            <h5 className="mb-1">Etiketter på spørsmål</h5>
            <div className="d-flex" style={{ minHeight: "2.4rem" }}>
              <Checkbox
                isChecked={checklist.itemTagsActive === true}
                setChecked={(e) =>
                  updateChecklist(
                    "itemTagsActive",
                    checklist.itemTagsActive,
                    checklist.itemTagsActive === true ? false : true,
                  )
                }
              />
              {checklist.itemTagsActive === true && (
                <>
                  <HSpace />
                  <SimpleTagsDeprecated
                    editable={removed === false}
                    activeTags={checklist.itemTags || []}
                    tagOptions={checklist.itemTags}
                    onChange={(itemTags) => {
                      const unique = [...new Set(itemTags)];
                      if (JSON.stringify(unique) !== JSON.stringify(checklist.itemTags)) {
                        updateChecklist("itemTags", checklist.itemTags, unique);
                      }
                    }}
                  />
                </>
              )}
            </div>
          </div>
          <VSpace />
          <div>
            <h5 className="mb-1">Aktiver IR på opprinnelige roller</h5>
            <Checkbox
              isChecked={checklist.includeIrOnDefaultRoles}
              setChecked={(e) =>
                updateChecklist(
                  "includeIrOnDefaultRoles",
                  checklist.includeIrOnDefaultRoles,
                  !checklist.includeIrOnDefaultRoles,
                )
              }
            />
          </div>
          <VSpace />
          <div>
            <h5 className="mb-1">Aktiver uavklart (UAV) på opprinnelige roller</h5>
            <Checkbox
              isChecked={checklist.includeUavOnDefaultRoles}
              setChecked={(e) =>
                updateChecklist(
                  "includeUavOnDefaultRoles",
                  checklist.includeUavOnDefaultRoles,
                  !checklist.includeUavOnDefaultRoles,
                )
              }
            />
          </div>
          <VSpace />
          <div>
            <h5 className="mb-1">Sjekklisteroller</h5>
            <ChecklistTypes
              checkTypes={checklist.checkTypes}
              editable={removed === false}
              onChange={(types) => updateChecklist("checkTypes", JSON.stringify(checklist.checkTypes), types)}
            />
          </div>
          <VSpace />
          {checklist.isTemplate === false && (
            <>
              <div>
                <h5 className="mb-1">Ansvarlig</h5>
                <McDropdownSingleUser
                  disabled={removed === true}
                  filterActive={membersInRedux.length > 10}
                  options={membersInRedux.map((member) => member.user as IUserDepricated)}
                  onChange={(user) => updateChecklist("assignedTo", checklist.assignedTo?.name, user)}
                  selected={checklist.assignedTo || { _id: "", name: "" }}
                />
              </div>
              <VSpace />
              <div>
                <h5 className="mb-1">Tidsfrist</h5>
                <McInput
                  type="date"
                  onChange={(e) =>
                    updateChecklist(
                      "dueDate",
                      checklist.dueDate,
                      e.target.value !== "" ? new Date(e.target.value) : undefined,
                    )
                  }
                  value={checklist.dueDate != null ? new Date(checklist.dueDate).toISOString() : null}
                  disabled={removed === true}
                />
              </div>
              <VSpace />
              <div>
                <h5 className="mb-1">PNS</h5>
                {pnsItems != null && (
                  <McTypeahead
                    id="pnstypeahead"
                    clearButton
                    defaultSelected={(() => {
                      return pnsItems.filter((p) => p.props.pnsId === checklist.pns && checklist.pns !== "");
                    })()}
                    labelKey="comboLabel"
                    options={pnsItems}
                    onChange={(items) => {
                      if (items[0] == null || items[0]?.props?.pnsId == null) {
                        updateChecklist("pns", checklist.pns, "");
                        return;
                      }

                      updateChecklist("pns", checklist.pns, items[0].props.pnsId);
                    }}
                    allowNew
                  />
                )}
              </div>
            </>
          )}
        </CardBody>
      </Card>

      <VSpace />

      <Card>
        <CardBody style={{ maxWidth: "35rem" }}>
          <CardTitle tag="h3">Rapportinfo</CardTitle>
          <div>
            <h5 className="mb-1">Fase i oppdraget</h5>
            <McInput
              onBlur={(e) => updateChecklist("phase", checklist.phase, e.target.value)}
              value={checklist.phase ?? ""}
              disabled={removed === true}
            />
          </div>
          <VSpace />
          <div>
            <h5 className="mb-1">Disiplin/fag</h5>
            <McInput
              onBlur={(e) => updateChecklist("discipline", checklist.discipline, e.target.value)}
              value={checklist.discipline ?? ""}
              disabled={removed === true}
            />
          </div>
          <VSpace />
          <div>
            <h5 className="mb-1">Navn/nr. kontrollert dokument/aktivitet</h5>
            <McInput
              onBlur={(e) => updateChecklist("activity", checklist.activity, e.target.value)}
              value={checklist.activity ?? ""}
              disabled={removed === true}
            />
          </div>
          <VSpace />
          <div>
            <h5 className="mb-1">Beskrivelse</h5>
            <McInput
              type="textarea"
              value={checklist.aboutText}
              onBlur={(e) => updateChecklist("aboutText", checklist.aboutText, e.target.value)}
              disabled={removed === true}
              min={3}
              dynamicHeight={true}
            />
          </div>
          <VSpace />
          <div>
            <h5 className="mb-1">Rapporttekst</h5>
            <StampConfiguration
              currentConfig={checklist.stampSettings}
              onChange={(newStampSettings) => updateChecklist("stampSettings", "", newStampSettings)}
            />
          </div>
        </CardBody>
      </Card>
      <VSpace />
      <ChecklistRemove disabled={removed === true} />
    </div>
  );
};

export default ChecklistAdmin;

export interface IDocumentManagers {
  preparedBy: IMinUserDepricated;
  checkedBy: IMinUserDepricated;
  approvedBy: IMinUserDepricated;
}

interface IDocumentManagerProps {
  documentManagers: IDocumentManagers;
  projectMembers: IMemberDepricated[];
  onSave: (data: IDocumentManagers) => void;
}

const DocumentManagers: React.FC<IDocumentManagerProps> = ({ onSave, documentManagers, projectMembers }) => {
  const saveChanges = (newData) => {
    onSave({
      preparedBy: newData[0].user,
      checkedBy: newData[1].user,
      approvedBy: newData[2].user,
    });
  };

  const _columns: ColDef[] = useMemo(() => {
    return [
      {
        headerName: "Rolle",
        field: "role",
        editable: false,
      },
      {
        headerName: "Bruker",
        field: "user",
        editable: true,
        cellRenderer: (params) => <UserCellRenderer height="15px" user={params.value} />,
        cellEditor: "agRichSelectCellEditor",
        valueGetter: (params) => {
          return params.data.user ?? null;
        },
        cellEditorParams: {
          cellRenderer: (params) => <UserCellRenderer height="15px" user={params.value} />,
          formatValue: (value) => value?.name ?? "",
          values: projectMembers.map((m) => m.user),
          allowTyping: true,
          filterList: true,
        },
      },
    ];
  }, [projectMembers]);

  const _data = useMemo(() => {
    return [
      {
        role: "Utarbeidet av",
        user: documentManagers?.preparedBy,
      },
      {
        role: "Kontrollert av",
        user: documentManagers?.checkedBy,
      },
      {
        role: "Godkjent av",
        user: documentManagers?.approvedBy,
      },
    ];
  }, [documentManagers]);

  return (
    <div>
      <div className="d-flex">
        <h5 className="mb-1">Dokumentansvarlige</h5>
        <UncontrolledTooltip placement="top" target="__ttdokumentansvarlige">
          Legg til flere brukere under "Prosjektmedlemmer"
        </UncontrolledTooltip>
        <p className="mr-2">
          <i id="__ttdokumentansvarlige" className="fa fa-info-circle fa-fw text-info" />
        </p>
      </div>
      <ViewEditTable
        gridOptions={{
          popupParent: document.body,
        }}
        data={_data}
        columns={_columns}
        onSave={saveChanges}
        newItemTemplateObject={{
          role: "",
          user: null,
        }}
        canEdit={true}
        canAddRows={false}
      />
    </div>
  );
};
